import { format } from 'date-fns';

export interface PayrollExportRow {
  employeeName: string;
  employeeType: 'technician' | 'staff';
  roleLabel: string;
  clockMinutes: number;
  afterHoursMinutes: number;
  producedMinutes: number;
  efficiency: number | null;
}

export interface PayrollDateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * Export payroll data as generic CSV
 */
export function exportPayrollCSV(
  data: PayrollExportRow[], 
  dateRange: PayrollDateRange,
  shopName?: string
): void {
  const headers = [
    'Employee',
    'Type',
    'Role',
    'Period Start',
    'Period End',
    'Clock Hours',
    'Produced Hours',
    'Efficiency %',
    'After Hours'
  ];

  const rows = data.map(row => [
    `"${row.employeeName}"`,
    row.employeeType === 'technician' ? 'Technician' : 'Staff',
    `"${row.roleLabel}"`,
    format(dateRange.startDate, 'yyyy-MM-dd'),
    format(dateRange.endDate, 'yyyy-MM-dd'),
    (row.clockMinutes / 60).toFixed(2),
    row.employeeType === 'technician' ? (row.producedMinutes / 60).toFixed(2) : '—',
    row.efficiency !== null ? `${row.efficiency}%` : '—',
    (row.afterHoursMinutes / 60).toFixed(2)
  ]);

  const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  downloadFile(csv, `payroll-${format(dateRange.startDate, 'yyyy-MM-dd')}-${format(dateRange.endDate, 'yyyy-MM-dd')}.csv`, 'text/csv');
}

/**
 * Export payroll data as QuickBooks Desktop IIF format (TIMEACT)
 * Note: This is a template - employee names must match QB Desktop exactly
 */
export function exportQuickBooksIIF(
  data: PayrollExportRow[], 
  dateRange: PayrollDateRange,
  clockEntriesByEmployee?: Map<string, { date: Date; hours: number }[]>
): void {
  // IIF Header
  const lines: string[] = [
    '!TIMEACT\tDATE\tJOB\tEMP\tITEM\tPITEM\tDURATION\tPROJ\tNOTE\tXFERTOPAYROLL\tBILLINGSTATUS'
  ];

  // If we have detailed clock entries, use them
  if (clockEntriesByEmployee) {
    clockEntriesByEmployee.forEach((entries, employeeName) => {
      entries.forEach(entry => {
        const hours = Math.floor(entry.hours);
        const minutes = Math.round((entry.hours - hours) * 60);
        const duration = `${hours}:${minutes.toString().padStart(2, '0')}`;
        
        lines.push(
          `TIMEACT\t${format(entry.date, 'MM/dd/yyyy')}\t""\t"${employeeName}"\t"Regular Hours"\t""\t${duration}\t""\t""\tN\t0`
        );
      });
    });
  } else {
    // Fall back to summary data (one row per employee for the period)
    data.forEach(row => {
      const totalHours = row.clockMinutes / 60;
      const hours = Math.floor(totalHours);
      const minutes = Math.round((totalHours - hours) * 60);
      const duration = `${hours}:${minutes.toString().padStart(2, '0')}`;
      
      lines.push(
        `TIMEACT\t${format(dateRange.startDate, 'MM/dd/yyyy')}\t""\t"${row.employeeName}"\t"Regular Hours"\t""\t${duration}\t""\t"Period: ${format(dateRange.startDate, 'MM/dd')}-${format(dateRange.endDate, 'MM/dd')}"\tN\t0`
      );
    });
  }

  const iifContent = lines.join('\r\n');
  downloadFile(iifContent, `payroll-qb-desktop-${format(dateRange.startDate, 'yyyy-MM-dd')}.iif`, 'text/plain');
}

/**
 * Export payroll data as QuickBooks Online Timesheet CSV format
 * Note: This is a template format - verify column headers match your QB Online import requirements
 */
export function exportQuickBooksOnlineCSV(
  data: PayrollExportRow[], 
  dateRange: PayrollDateRange,
  clockEntriesByEmployee?: Map<string, { date: Date; hours: number; notes?: string }[]>
): void {
  const headers = ['Date', 'Employee', 'Hours Worked', 'Notes'];
  const rows: string[][] = [];

  // If we have detailed clock entries, use them
  if (clockEntriesByEmployee) {
    clockEntriesByEmployee.forEach((entries, employeeName) => {
      entries.forEach(entry => {
        rows.push([
          format(entry.date, 'MM/dd/yyyy'),
          `"${employeeName}"`,
          entry.hours.toFixed(2),
          entry.notes ? `"${entry.notes}"` : ''
        ]);
      });
    });
  } else {
    // Fall back to summary data
    data.forEach(row => {
      rows.push([
        format(dateRange.startDate, 'MM/dd/yyyy'),
        `"${row.employeeName}"`,
        (row.clockMinutes / 60).toFixed(2),
        `"Period: ${format(dateRange.startDate, 'MM/dd')} - ${format(dateRange.endDate, 'MM/dd')}"`
      ]);
    });
  }

  const csv = [headers.join(','), ...rows.map(row => row.join(','))].join('\n');
  downloadFile(csv, `payroll-qb-online-${format(dateRange.startDate, 'yyyy-MM-dd')}.csv`, 'text/csv');
}

/**
 * Helper function to download a file
 */
function downloadFile(content: string, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
